import React from 'react';

import { Filter } from './search_filters';
import FilterChip from './filter_chip';
import { useUser, useExpEnabled } from '../user_hooks';
import { isDefaultedSellerLocationFilter } from './seller_location_search_filter';
import { useURL } from '../url_context';
import MarketplaceFilter from './marketplace_filter';
import MarketplaceDismissibleFilterRow from './marketplace_dismissible_filter_row';
import { Location } from 'history';
import { isSomethingSelected } from '../facet_utils';
import experiments from '../experiments';
import MarketplaceFilterSidebar from './marketplace_filter_sidebar';

interface IProps {
  filters?: Filter[];
  loading?: boolean;
  trackingQuery?: string;
  displayCount?: boolean;
  dynamicTitle?: boolean;
  hideOnMobile?: boolean;
  location: Location;
  totalText?: string;
}

export const FEATURED = [
  'CONDITION_SLUGS',
  'CATEGORY_SLUGS',
  'BRAND_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

export const MODEL_FAMILY_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

export function excludedFilters(pathname) {
  const EXCLUDED = ['SORT_BY'];
  if (pathname?.includes('/marketplace')) {
    EXCLUDED.push('TEXT_QUERY');
  }
  return EXCLUDED;
}

export default function MarketplaceFilterRow({
  filters = [],
  loading = false,
  trackingQuery = '',
  displayCount = false,
  dynamicTitle = false,
  hideOnMobile = false,
  location = null,
  totalText = '',
}: IProps) {
  const user = useUser();
  const { pathname } = useURL();
  const inMarketplaceDismissibleFiltersExp = useExpEnabled(experiments.MARKETPLACE_DISMISSIBLE_FILTERS);

  if (loading && filters.length === 0) {
    return (
      <div className="search-overview__filters">
        {FEATURED.map(f => {
          return <Placeholder key={f} />;
        })}
      </div>
    );
  }

  const defaultFilters = getFilters(filters, user, pathname);

  const defaultFilterKeys = defaultFilters.map(f => {
    return f.aggregationName ? f.aggregationName : f.key;
  });

  const otherSelectedFilters = filters.filter(f => {
    const key = getFilterKey(f);
    if (defaultFilterKeys.includes(key)) return false;
    if (excludedFilters(pathname).includes(key)) return false;
    return f.options && f.options.some(o => {
      if (o.all) return false;
      // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
      if (key === 'SHIPS_TO' && o.optionValue === user.shippingRegionCode) return false;
      return o.selected;
    });
  });

  const allSelectedFilters = filters.filter(f => {
    const key = f.aggregationName || f.key;
    if (excludedFilters(pathname).includes(key)) return false;
    if (key === 'SORT_BY') return false;

    // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
    if (key === 'SHIPS_TO') {
      if (f.options.some(o => {
        return o.optionValue === user.shippingRegionCode;
      })) return false;
      return true;
    }
    return isSomethingSelected(f, true);
  });

  return (
    <>
      <div className="search-overview__filters">
        {defaultFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
            />
          );
        })}
        {dynamicTitle && otherSelectedFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
            />
          );
        })}
        <MarketplaceFilterSidebar
          filters={filters}
          loading={loading}
          subtitle={totalText}
          trackingQuery={trackingQuery}
        />
      </div>
      { inMarketplaceDismissibleFiltersExp &&
        <div className="search-overview__mobile-overflow">
          <div className="search-overview__filters search-overview__filters--dismissible">
            <MarketplaceDismissibleFilterRow
              key={'dismissible-filters'}
              filters={allSelectedFilters}
              location={location}
            />
          </div>
        </div>
      }
    </>
  );
}

function Placeholder() {
  return (
    <div className="mr-1">
      <FilterChip loading>
        <div className="filter-chip__loading-placeholder" />
      </FilterChip>
    </div>
  );
}

function getFilterKey(f: Filter) {
  return f.aggregationName || f.key;
}

function sortFeatured(a: Filter, b: Filter) {
  const keyA = getFilterKey(a);
  const keyB = getFilterKey(b);
  const indexOfA = FEATURED.indexOf(keyA);
  const indexOfB = FEATURED.indexOf(keyB);
  if (indexOfA < indexOfB) { return -1; }
  if (indexOfA > indexOfB) { return 1; }
  return 0;
}

function getFilters(filters, user, pathname) {
  const featuredFilters = filters.filter(f => {
    const key = getFilterKey(f);
    const isFeatured = pathname?.includes('/f/')
      ? MODEL_FAMILY_FEATURED.includes(key)
      : FEATURED.includes(key);
    return isFeatured || isDefaultedSellerLocationFilter(key, user);
  });

  if (featuredFilters.length === 0) {
    return filters.filter(f => {
      const key = getFilterKey(f);
      return !(excludedFilters(pathname).includes(key));
    }).slice(0, 5);
  }

  return featuredFilters.sort(sortFeatured);
}
